import type { BetId, HeroClass, IncursionRisk, SideId } from '@somnhold/content';
import { campaignDay, momentAt, riteClosesAt, waveAt, type CalendarSpec, type ChatLine, type Moment } from '@somnhold/protocol';
import {
  advanceTime,
  applySima,
  settleSimaWeek,
  simaParams,
  calendarMarea,
  applyCampaignCommand,
  applyDungeon,
  applyIncursion,
  createBattle,
  createCampaign,
  dungeonParams,
  incursionParams,
  seasonOf,
  resolveWave,
  waveBattleParams,
  type BattleParams,
  waveSky,
  upgradeState,
  ensureSkillTree,
  snapshot,
  type BattleState,
  type CampaignCommand,
  type CampaignState,
  type Command,
  type CommandResult,
  applySagaBattle,
  closeSagaVote,
  closeSideVote,
  rerunBattleParams,
  sagaBattleParams,
  betBattleParams,
  sideBattleParams,
  type SagaTicket,
} from '@somnhold/sim';

export interface PlayedBattle {
  marea: number;
  wave: number;
  sector: number;
  battle: BattleState;
  log: { tick: number; cmds: Command[] }[];
}

/** A live party gathering or under way (online campaigns). */
export interface PartyInfo {
  id: string;
  /** A wave room is one sector of the coming wave, gathering before its hour. */
  kind: 'crypt' | 'incursion' | 'wave' | 'saga' | 'expedition' | 'maze';
  leader: string;
  node?: number;
  sector?: number;
  players: string[];
  started: boolean;
}

/** Who has the campaign open right now and which live parties exist. */
export interface Presence {
  online: string[];
  rooms: PartyInfo[];
  /** Where online players' heroes stand in the bastion. */
  pos?: Record<string, { x: number; y: number }>;
  /** Sectors of the coming wave already defended, by whom and how. */
  played?: Record<string, { pid: string; source: string }>;
  /** The keep's chat, its last lines. */
  chat?: ChatLine[];
}

/** The sector a player defends by default: their ghost's post, else the first sector they adopted. */
export const homeSector = (s: CampaignState, me: string): number => {
  const p = s.players.find((x) => x.id === me);
  const post = p?.ghostPost;
  if (post && 'sector' in post) return post.sector;
  return (p?.adoptedSectors[0] ?? 0) % Math.max(1, s.map.sectors.length);
}

/** What the screens need from a campaign, whether it lives in this browser or on the server. */
export interface CampaignService {
  readonly id: string;
  readonly me: string;
  readonly kind: 'local' | 'server';
  state: CampaignState;
  calendar(): CalendarSpec;
  now(): number;
  moment(): Moment;
  command(c: CampaignCommand): Promise<CommandResult>;
  /** Minutes of site work the server would still accept right now (it checks work against the session's clock). */
  workAllowance(): number;
  /** Builds the battle for my sector of the next wave (live or "adelanto"). */
  prepareBattle(sector: number): Promise<BattleState>;
  /** Leaving a live battle without finishing it releases the wave. */
  abandonBattle(): Promise<void>;
  submitBattle(p: PlayedBattle): Promise<void>;
  /** Resolve whatever is due; returns true if the state changed. */
  poll(): Promise<boolean>;
  /** Pulls the latest state now (online): a vote under way follows the others' votes with it. */
  refresh?(): Promise<boolean>;
  subscribe(fn: () => void): () => void;
  /** Whether my sector of the next wave can be played now (live or within the preview window). */
  canPlayNext(): 'live' | 'preview' | false;
  /** I already played my sector of the next wave ahead of time; it counts when the wave arrives. */
  playedNext(): boolean;
  /** While I am in the bastion a due wave waits for me instead of resolving with ghosts (local only). */
  attend(on: boolean): void;
  /** Let the ghosts defend the wave that is due now. */
  release(): Promise<void>;
  /** Campaign day (incursion nodes change at the group's midnight). */
  day(): number;
  /** An incursion on one of today's nodes, optionally carrying up to three friends' ghosts. */
  prepareIncursion(node: number, companions: string[], risk: IncursionRisk, tier?: 1 | 2 | 3): Promise<{ id: string; battle: BattleState }>;
  /** Last known presence (null for local campaigns). */
  presence(): Presence | null;
  /** Local campaigns: the crypt, alone (online it is a live room for 2 to 4). */
  prepareDungeon(tier?: 1 | 2 | 3, party?: string[], ghosts?: string[]): BattleState;
  submitDungeon(battle: BattleState, tier?: 1 | 2 | 3): Promise<void>;
  /** Local campaigns: a run of the Chasm from `start` (online it is a live room for 1 to 8). */
  prepareSima(start: number): BattleState;
  submitSima(battle: BattleState): Promise<void>;
  submitIncursion(id: string, battle: BattleState, log: { tick: number; cmds: Command[] }[]): Promise<void>;
  /** The saga's next battle act to play solo (`rerun`: the act this hero is at in a finished chapter played again). */
  /** `bet`: one of Brann and Hilde's bets instead of the saga's next act. */
  /** `ghosts`: the absent friends the dragon's fight brings as spectres. */
  prepareSaga(rerun?: boolean, bet?: BetId, side?: SideId, ghosts?: string[], replay?: number): Promise<{ id: string; battle: BattleState }>;
  submitSaga(id: string, battle: BattleState, log: { tick: number; cmds: Command[] }[]): Promise<void>;
}

const KEY = (id: string): string => `somnhold.local.${id}`;
const INDEX = 'somnhold.local.index';

export interface LocalMeta {
  id: string;
  name: string;
  createdAt: number;
  dayMs?: number;
}

const readIndex = (): LocalMeta[] => {
  try {
    return JSON.parse(localStorage.getItem(INDEX) ?? '[]') as LocalMeta[];
  } catch {
    return [];
  }
};

export const listLocal = (): LocalMeta[] => readIndex();

/** How a stored local campaign stands, without loading it: for the menu. */
export const localStatus = (id: string): { status: string; marea: number } | null => {
  try {
    const s = JSON.parse(localStorage.getItem(KEY(id)) ?? 'null') as CampaignState | null;
    return s ? { status: s.status, marea: s.mareaIndex } : null;
  } catch {
    return null;
  }
};

/**
 * A campaign stored in this browser. Companions are AI ghosts so a single person can try the whole
 * loop; `dayMs` compresses the calendar for testing (a "day" can last minutes).
 */
export class LocalCampaign implements CampaignService {
  readonly kind = 'local' as const;
  private listeners = new Set<() => void>();
  private pending = new Map<string, PlayedBattle & { params?: BattleParams }>();
  private opened: BattleParams | null = null;
  private attending = false;
  private released: string | null = null;

  private constructor(
    public state: CampaignState,
    readonly me: string,
    private meta: LocalMeta,
  ) {}

  get id(): string {
    return this.state.id;
  }

  static create(opts: { name: string; heroClass: HeroClass; companions: { name: string; cls: HeroClass }[]; dayMs?: number; lang: 'es' | 'en'; /** Terraced land and the keep alone (docs/CONSTRUCCION.md). */ land?: boolean }): LocalCampaign {
    const now = Date.now();
    const id = `local-${now.toString(36)}`;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const seed = (now ^ 0x9e3779b9) >>> 0;
    const state = createCampaign({
      id,
      seed,
      createdAt: now,
      timezone: tz,
      lang: opts.lang,
      ...(opts.land ? { land: true } : {}),
      players: [
        { id: 'me', displayName: opts.name, heroClass: opts.heroClass },
        ...opts.companions.map((c, i) => ({ id: `ally${i + 1}`, displayName: c.name, heroClass: c.cls })),
      ],
    });
    const meta: LocalMeta = { id, name: opts.name, createdAt: now, ...(opts.dayMs ? { dayMs: opts.dayMs } : {}) };
    const c = new LocalCampaign(state, 'me', meta);
    c.save();
    try {
      localStorage.setItem(INDEX, JSON.stringify([meta, ...readIndex().filter((m) => m.id !== id)].slice(0, 6)));
    } catch {
      /* storage full or blocked: the campaign still works for this tab */
    }
    return c;
  }

  static load(id: string): LocalCampaign | null {
    try {
      const raw = localStorage.getItem(KEY(id));
      const meta = readIndex().find((m) => m.id === id);
      if (!raw || !meta) return null;
      const state = JSON.parse(raw) as CampaignState;
      // Saves from before the breakable ring wall get it now.
      if (upgradeState(state)) state.version++;
      if (ensureSkillTree(state)) state.version++;
      return new LocalCampaign(state, 'me', meta);
    } catch {
      return null;
    }
  }

  static remove(id: string): void {
    try {
      localStorage.removeItem(KEY(id));
      localStorage.setItem(INDEX, JSON.stringify(readIndex().filter((m) => m.id !== id)));
    } catch {
      /* ignore */
    }
  }

  private save(): void {
    try {
      localStorage.setItem(KEY(this.state.id), JSON.stringify(snapshot(this.state)));
    } catch {
      /* ignore */
    }
  }

  calendar(): CalendarSpec {
    return { createdAt: this.state.createdAt, timezone: this.state.timezone, waveHours: this.state.waveHours, ...(this.meta.dayMs ? { dayMs: this.meta.dayMs } : {}) };
  }

  /** Test/dev clock offset (local campaigns only). */
  private skew = 0;

  now(): number {
    return Date.now() + this.skew;
  }

  /** Jump the clock forward (tests, demos). */
  skip(ms: number): void {
    this.skew += ms;
    void this.poll();
  }

  moment(): Moment {
    return momentAt(this.calendar(), this.state.mareaIndex, this.state.waveIndex, this.now(), this.state.status === 'active', calendarMarea(this.state));
  }

  private emit(): void {
    for (const f of this.listeners) f();
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  async command(c: CampaignCommand): Promise<CommandResult> {
    // The rite closes by the calendar, as on the server (engine.ts).
    if ((c.t === 'rite' || c.t === 'riteUndo') && this.now() >= riteClosesAt(this.calendar(), calendarMarea(this.state))) return { ok: false, reason: 'riteClosed' };
    const r = applyCampaignCommand(this.state, this.me, c, this.now(), this.day());
    // The companions are bots: they go along with the player's choice, nothing to wait for.
    if (r.ok && this.state.saga?.vote) closeSagaVote(this.state, this.now());
    // The side sagas' votes too: offline, the companions go along.
    for (const id of Object.keys(this.state.sides ?? {})) if (r.ok && this.state.sides?.[id]?.vote) closeSideVote(this.state, id as SideId, this.now());
    // Companions are "active" as long as the campaign is being played.
    for (const p of this.state.players) if (p.id !== this.me) p.lastActiveAt = this.now();
    this.save();
    this.emit();
    return r;
  }

  /** The omen over this Marea's wave `w`: the one of the day it is scheduled on (docs/PRESAGIOS.md §5). */
  private sky(w: number): ReturnType<typeof waveSky> {
    const at = waveAt(this.calendar(), this.state.mareaIndex, w);
    return waveSky(this.state, campaignDay(this.calendar(), at), at);
  }

  async prepareBattle(): Promise<BattleState> {
    if (this.canPlayNext() === 'live') this.hold(true);
    // Kept to write back only what the battle spent when the wave resolves (docs/PRESAGIOS.md §3.3).
    this.opened = waveBattleParams(this.state, this.now(), { live: [this.me], sky: this.sky(this.state.waveIndex) });
    return createBattle(this.opened);
  }

  async abandonBattle(): Promise<void> {
    this.hold(false);
  }

  playedNext(): boolean {
    const k = `${this.state.mareaIndex}:${this.state.waveIndex}:`;
    return [...this.pending.keys()].some((x) => x.startsWith(k));
  }

  attend(on: boolean): void {
    this.attending = on;
  }

  async release(): Promise<void> {
    this.released = `${this.state.mareaIndex}:${this.state.waveIndex}`;
    await this.poll();
  }

  canPlayNext(): 'live' | 'preview' | false {
    // With the keep fallen the rest of the Marea is not fought.
    if (this.state.status !== 'active' || this.state.mareaLost || this.playedNext()) return false;
    const at = waveAt(this.calendar(), calendarMarea(this.state), this.state.waveIndex);
    const now = this.now();
    // No playing ahead of time: the wave is fought when it comes.
    return now >= at ? 'live' : false;
  }

  async submitBattle(p: PlayedBattle): Promise<void> {
    this.pending.set(`${p.marea}:${p.wave}:${p.sector}`, this.opened ? { ...p, params: this.opened } : p);
    this.opened = null;
    this.holding = null;
    await this.poll();
  }

  day(): number {
    return campaignDay(this.calendar(), this.now());
  }

  private incursions = new Map<string, { node: number; day: number; companions: string[]; risk: IncursionRisk; tier: 1 | 2 | 3 }>();

  async prepareIncursion(node: number, companions: string[], risk: IncursionRisk, tier: 1 | 2 | 3 = 1): Promise<{ id: string; battle: BattleState }> {
    const day = this.day();
    const found = incursionParams(this.state, this.me, node, day, companions, risk, [], tier);
    if (!found) throw new Error('noNode');
    const params = { ...found, ...seasonOf(this.now()) };
    const id = `i${day}-${node}-${Date.now()}`;
    this.incursions.set(id, { node, day, companions, risk, tier });
    return { id, battle: createBattle(params) };
  }

  workAllowance(): number {
    return Infinity;
  }

  presence(): Presence | null {
    return null;
  }

  /** `party`: who goes down besides me (tools/perf/party-crypt.mjs measures a big party); `ghosts`: companions as spectres. */
  prepareDungeon(tier: 1 | 2 | 3 = 1, party: string[] = [], ghosts: string[] = []): BattleState {
    return createBattle({ ...(dungeonParams(this.state, [this.me, ...party], this.day(), tier, ghosts) as Parameters<typeof createBattle>[0]), ...seasonOf(this.now()) });
  }

  async submitDungeon(battle: BattleState, tier: 1 | 2 | 3 = 1): Promise<void> {
    applyDungeon(this.state, battle, this.day(), this.now(), tier);
    this.save();
    this.emit();
  }

  prepareSima(start: number, party: string[] = []): BattleState {
    settleSimaWeek(this.state, this.day(), this.now());
    return createBattle({ ...(simaParams(this.state, [this.me, ...party], this.day(), start) as Parameters<typeof createBattle>[0]), ...seasonOf(this.now()) });
  }

  async submitSima(battle: BattleState): Promise<void> {
    applySima(this.state, battle, this.day(), this.now());
    this.save();
    this.emit();
  }

  async submitIncursion(id: string, battle: BattleState): Promise<void> {
    const inc = this.incursions.get(id);
    if (!inc) return;
    this.incursions.delete(id);
    applyIncursion(this.state, this.me, inc.node, inc.day, inc.companions, battle, this.now(), inc.risk, [], inc.tier);
    const me = this.state.players.find((p) => p.id === this.me);
    if (me) me.lastActiveAt = this.now();
    this.save();
    this.emit();
  }

  private sagas = new Map<string, SagaTicket>();

  async prepareSaga(rerun = false, bet?: BetId, side?: SideId, ghosts: string[] = [], replay?: number): Promise<{ id: string; battle: BattleState }> {
    const found = side ? sideBattleParams(this.state, [this.me], side, replay) : bet ? betBattleParams(this.state, [this.me], bet) : rerun ? rerunBattleParams(this.state, this.me, [], ghosts) : sagaBattleParams(this.state, [this.me], this.day(), undefined, ghosts);
    if (!found) throw new Error('sagaClosed');
    const id = `s${found.ticket.chapter}-${found.ticket.act}-${Date.now()}`;
    this.sagas.set(id, found.ticket);
    return { id, battle: createBattle({ ...found.params, ...seasonOf(this.now()) }) };
  }

  async submitSaga(id: string, battle: BattleState): Promise<void> {
    const ticket = this.sagas.get(id);
    if (!ticket) return;
    this.sagas.delete(id);
    applySagaBattle(this.state, [this.me], ticket, battle, this.now(), this.day());
    this.state.version++;
    this.save();
    this.emit();
  }

  /** While I play the due wave live, it waits for me instead of resolving offline. */
  private holding: string | null = null;

  hold(on: boolean): void {
    this.holding = on ? `${this.state.mareaIndex}:${this.state.waveIndex}` : null;
  }

  async poll(): Promise<boolean> {
    let changed = false;
    const now = this.now();
    for (const p of this.state.players) if (p.id !== this.me) p.lastActiveAt = now;
    let guard = 0;
    while (this.state.status === 'active' && guard++ < 40) {
      const m = this.state.mareaIndex;
      const w = this.state.waveIndex;
      const at = waveAt(this.calendar(), m, w);
      if (now < at || this.holding === `${m}:${w}`) break;
      // Someone in the bastion gets to choose: defend it, or let the ghosts do it.
      if (this.attending && !this.state.mareaLost && this.released !== `${m}:${w}` && !this.playedNext()) break;
      const mine = [...this.pending.values()].find((x) => x.marea === m && x.wave === w);
      resolveWave(this.state, now > at ? now : at, { ...(mine ? { played: { battle: mine.battle, source: 'live' as const, ...(mine.params ? { params: mine.params } : {}) } } : { sky: this.sky(w) }), day: campaignDay(this.calendar(), now > at ? now : at) });
      for (const k of [...this.pending.keys()]) if (k.startsWith(`${m}:${w}:`)) this.pending.delete(k);
      changed = true;
    }
    // A new week of the Chasm: whoever went down last week gets its chest.
    if (settleSimaWeek(this.state, this.day(), now)) changed = true;
    const before = this.state.clock;
    advanceTime(this.state, now);
    if (this.state.clock !== before) changed = true;
    if (changed) {
      this.save();
      this.emit();
    }
    return changed;
  }
}
